/* eslint-disable */
import { onMounted, onUnmounted } from "vue";
import makercartLogService from "@/services/makercartLogService";
import {
  LOCALE_STORAGE_KEY,
  MAKERCART_SEQUENCE_STATE,
} from "@/utils/constants";

const SESSION_ID_KEY = "makercart_cart_session_id";
const TAB_COUNT_KEY = "makercart_cart_tab_count";
const STEPS_KEY = "makercart_cart_logged_steps";
const TAB_ACTIVE_FLAG_KEY = "makercart_cart_tab_active"; // sessionStorage per-tab
const DEC_DONE_KEY = "makercart_cart_dec_done"; // sessionStorage per-tab

function generateSessionId() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (_) {}
  return `mc_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

async function getRegion() {
  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    return (saved || "US").toUpperCase();
  } catch (_) {
    return "US";
  }
}

function getSessionId() {
  try {
    return localStorage.getItem(SESSION_ID_KEY);
  } catch (_) {
    return null;
  }
}

function setSessionId(id) {
  try {
    localStorage.setItem(SESSION_ID_KEY, id);
  } catch (_) {}
}

function getTabCount() {
  try {
    return parseInt(localStorage.getItem(TAB_COUNT_KEY) || "0", 10) || 0;
  } catch (_) {
    return 0;
  }
}

function setTabCount(count) {
  try {
    localStorage.setItem(TAB_COUNT_KEY, String(Math.max(0, count)));
  } catch (_) {}
}

function getLoggedSteps() {
  try {
    const raw = localStorage.getItem(STEPS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
}

function setLoggedSteps(steps) {
  try {
    localStorage.setItem(STEPS_KEY, JSON.stringify(steps || {}));
  } catch (_) {}
}

function clearSessionState() {
  try {
    localStorage.removeItem(SESSION_ID_KEY);
    localStorage.removeItem(STEPS_KEY);
    localStorage.removeItem(TAB_COUNT_KEY);
  } catch (_) {}
}

async function startStepIfNeeded(triggerPoint) {
  const steps = getLoggedSteps();
  //   if (steps[triggerPoint]) return;

  const sessionId = getSessionId();
  if (!sessionId) return;

  try {
    const region = await getRegion();
    await makercartLogService.startSession({
      session_id: sessionId,
      region: region,
      trigger_point: triggerPoint,
    });
    steps[triggerPoint] = true;
    setLoggedSteps(steps);
  } catch (err) {
    // Swallow errors to avoid impacting UX
    console.error("startSession error:", err);
  }
}

function endSessionBeacon(sessionId) {
  try {
    const apiBase = process.env.VUE_APP_API_URL || "";
    const dropOffUrl = `${apiBase}/api/cart-logs/drop-off`;
    const startSessionUrl = `${apiBase}/api/cart-logs`; // POST for startSession

    const steps = getLoggedSteps();

    // Helper to safely get region synchronously (matches getRegion logic but sync)
    const getRegionSync = () => {
      try {
        const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
        return (saved || "US").toUpperCase();
      } catch (_) {
        return "US";
      }
    };

    // 1. Helper function to send step beacons
    const sendStepBeacon = (triggerPoint) => {
      if (!steps[triggerPoint]) return;

      const region = getRegionSync();
      const payload = JSON.stringify({
        session_id: sessionId,
        region: region,
        trigger_point: triggerPoint,
      });

      fetch(startSessionUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
        mode: "cors",
      }).catch((err) =>
        console.error(`endSessionBeacon (${triggerPoint}) error:`, err)
      );
    };

    // 2. Fire beacons for View and Process if needed
    sendStepBeacon(MAKERCART_SEQUENCE_STATE.View);
    sendStepBeacon(MAKERCART_SEQUENCE_STATE.Process);

    // 2. Fire the drop-off beacon
    const dropOffPayload = JSON.stringify({ session_id: sessionId });

    fetch(dropOffUrl, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: dropOffPayload,
      keepalive: true,
      mode: "cors",
    }).catch((err) => {
      console.error("endSessionBeacon (drop-off) fetch error:", err);
      // Fallback mainly if keepalive isn't supported or some other weirdness,
      // though typically fetch failure on unload is final.
      makercartLogService.endSession({ session_id: sessionId }).catch(() => {});
    });
  } catch (err) {
    console.error("endSessionBeacon error:", err);
    try {
      makercartLogService.endSession({ session_id: sessionId }).catch(() => {});
    } catch (_) {}
  }
}

const HEARTBEAT_KEY = "makercart_cart_heartbeat";
const STALE_THRESHOLD_MS = 5000;

function updateHeartbeat() {
  try {
    localStorage.setItem(HEARTBEAT_KEY, Date.now().toString());
  } catch (_) {}
}

export function useMakercartSession() {
  let heartbeatInterval = null;

  function ensureSession() {
    // Check for stale session from crash/force-close
    try {
      const lastHeartbeat = parseInt(
        localStorage.getItem(HEARTBEAT_KEY) || "0",
        10
      );
      const now = Date.now();

      // If heartbeat is too old, previous session is dead -> clear it
      if (lastHeartbeat > 0 && now - lastHeartbeat > STALE_THRESHOLD_MS) {
        console.log(
          "[MakercartSession] Stale session detected, clearing state"
        );
        clearSessionState();
        setTabCount(0); // Force reset tab count
      }
    } catch (_) {}

    const currentCount = getTabCount();
    if (!getSessionId() || currentCount <= 0) {
      const newId = generateSessionId();
      setSessionId(newId);
      setLoggedSteps({});
      setTabCount(0);
    }
  }

  function incrementTabsAndMaybeLogView() {
    try {
      const alreadyActive = sessionStorage.getItem(TAB_ACTIVE_FLAG_KEY) === "1";
      if (!alreadyActive) {
        const before = getTabCount();
        setTabCount(before + 1);
        sessionStorage.setItem(TAB_ACTIVE_FLAG_KEY, "1");
        sessionStorage.removeItem(DEC_DONE_KEY);
        startStepIfNeeded(MAKERCART_SEQUENCE_STATE.View);
      }
    } catch (_) {}
  }

  function decrementTabsAndMaybeEnd(reason = "unknown") {
    try {
      const wasActive = sessionStorage.getItem(TAB_ACTIVE_FLAG_KEY) === "1";
      const alreadyDecremented = sessionStorage.getItem(DEC_DONE_KEY) === "1";
      if (!wasActive || alreadyDecremented) return;

      sessionStorage.setItem(DEC_DONE_KEY, "1");
      sessionStorage.setItem(TAB_ACTIVE_FLAG_KEY, "0");

      const after = Math.max(0, getTabCount() - 1);
      setTabCount(after);
      if (after === 0) {
        const sessionId = getSessionId();
        if (sessionId) {
          endSessionBeacon(sessionId);
        }
        clearSessionState();
        // Clear heartbeat on clean exit
        try {
          localStorage.removeItem(HEARTBEAT_KEY);
        } catch (_) {}
      }
    } catch (err) {
      console.error("[MakercartSession] decrement error", err);
    }
  }

  async function trackProcessStep() {
    await startStepIfNeeded(MAKERCART_SEQUENCE_STATE.View);
    await startStepIfNeeded(MAKERCART_SEQUENCE_STATE.Process);
  }

  async function trackQuoteGeneratedStep(
    trackView = false,
    trackProcess = false
  ) {
    if (trackView) {
      await startStepIfNeeded(MAKERCART_SEQUENCE_STATE.View);
    }
    if (trackProcess) {
      await startStepIfNeeded(MAKERCART_SEQUENCE_STATE.Process);
    }
    await startStepIfNeeded(MAKERCART_SEQUENCE_STATE.Complete);
  }

  function handleTabClose() {
    decrementTabsAndMaybeEnd("tab-close");
  }

  onMounted(() => {
    ensureSession();
    incrementTabsAndMaybeLogView();
    // Start heartbeat
    updateHeartbeat();
    heartbeatInterval = setInterval(updateHeartbeat, 2000);

    window.addEventListener("beforeunload", handleTabClose);
    window.addEventListener("pagehide", handleTabClose);
  });

  onUnmounted(() => {
    if (heartbeatInterval) clearInterval(heartbeatInterval);
    window.removeEventListener("beforeunload", handleTabClose);
    window.removeEventListener("pagehide", handleTabClose);
    // Don't decrement on SPA navigation - only on actual tab close
  });

  return {
    trackProcessStep,
    trackQuoteGeneratedStep,
  };
}
